import type { AssemblyDocument } from '../assembly-document/assembly-document-types'

/**
 * 装配草稿文档记录。
 */
export interface AssemblyDraftDocumentRecord {
  /**
   * 当前装配根节点 key。
   */
  readonly rootNodeKey: string
  /**
   * 草稿基于的父装配提交 ID，用于后续提交并发保护。
   */
  readonly baseCommitId: string
  /**
   * 打开项目时下载并解析得到的只读基线文档。
   */
  readonly baseAssemblyDocument: AssemblyDocument
  /**
   * 当前内存中可修改的工作文档。
   */
  readonly workingAssemblyDocument: AssemblyDocument
}

/**
 * 当前 project-model runtime 内的装配草稿文档仓库。
 *
 * 该 Store 只保存 `_assembly.json` 的 base/working 文档，不承载下载、
 * 设计树、选择高亮、变更判断或提交后重置逻辑。
 */
export class AssemblyDraftDocumentStore {
  private readonly records = new Map<string, AssemblyDraftDocumentRecord>()

  /**
   * 读取指定根节点的装配草稿文档记录。
   *
   * @param rootNodeKey 当前装配根节点 key。
   * @returns 草稿文档记录；不存在时返回 null。
   */
  get(rootNodeKey: string): AssemblyDraftDocumentRecord | null {
    return this.records.get(rootNodeKey) ?? null
  }

  /**
   * 写入或替换指定根节点的草稿文档记录。
   *
   * @param record 下一版草稿文档记录。
   */
  set(record: AssemblyDraftDocumentRecord): void {
    this.records.set(record.rootNodeKey, record)
  }

  /**
   * 仅更新工作文档，保留 base 文档和 base commit。
   *
   * @param rootNodeKey 当前装配根节点 key。
   * @param nextDocument 下一版工作装配文档。
   * @throws 当指定根节点没有对应的装配草稿记录时。
   */
  updateWorkingDocument(rootNodeKey: string, nextDocument: AssemblyDocument): void {
    const record = this.records.get(rootNodeKey)

    if (!record) {
      throw new Error(`assembly draft document record not found: ${rootNodeKey}`)
    }

    this.records.set(rootNodeKey, {
      ...record,
      workingAssemblyDocument: nextDocument,
    })
  }

  /**
   * 移除指定根节点的草稿文档记录。
   *
   * @param rootNodeKey 当前装配根节点 key。
   */
  remove(rootNodeKey: string): void {
    this.records.delete(rootNodeKey)
  }

  /**
   * 清空所有装配草稿文档记录。
   */
  clear(): void {
    this.records.clear()
  }
}
